import React, { useState } from 'react';
import Image from 'next/legacy/image';
import cx from 'classnames';
import { useSpring, animated } from '@react-spring/web';

import { getImageDataWithHttps } from '../../../../../utils/contentful';
import { useScrollPosition } from '../../../../../hooks/useScrollPosition';

import CtaWidget from '../../../../CtaWidget';
import Link from '../../../../Link';
import { ProfileDropdownMenu } from '../../../ProfileDropdownMenu';

import { MobileNavProps } from '../index';

import { LogoWrapper, MobileNavbarWrapper } from '../MobileNav.styles';

import defaultFullLogo from '@/images/gl-logo-full-large.svg';

function Hamburger(props: { active: boolean; onClick: () => void }) {
  const active = props.active;

  return (
    <button
      className="bg-transparent relative z-40 m-0 flex h-8 w-8 flex-col items-center justify-center border-0 p-0"
      onClick={props.onClick}
      data-testid="hamburger-menu"
      data-blockid="open-hamburger"
    >
      <div
        className={cx(
          'absolute h-[3px] w-5 bg-[#1D252C] transition-all duration-300 ease-in-out',
          active ? 'rotate-45' : '-translate-y-[5px]'
        )}
      />
      <div
        className={cx(
          'absolute h-[3px] w-5 bg-[#1D252C] transition-all duration-300 ease-in-out',
          active ? '-rotate-45' : 'translate-y-[5px]'
        )}
      />
    </button>
  );
}

interface Props extends MobileNavProps {
  isMenuOpen: boolean;
  toggleModal: () => void;
}

const defaultFullLogoData = {
  ...defaultFullLogo,
  alt: 'Greenlight logo'
};

export function MobileNavbar({
  isSimplified,
  isScrolled,
  isMenuOpen,
  mobileCta,
  scrolledMobileCta,
  bottomMobileCta,
  abbreviatedLogoMobile,
  fullLogoMobile,
  linkToHome,
  toggleModal,
  showAuthenticatedUser
}: Props) {
  const [ctaScrollPosition, setCtaScrollPosition] = useState(false);

  useScrollPosition(
    ({ currPos }) => {
      if (bottomMobileCta) {
        if (currPos.y > 412) setCtaScrollPosition(true);
        else if (currPos.y <= 412) setCtaScrollPosition(false);
      }
    },
    [ctaScrollPosition, bottomMobileCta],
    undefined,
    true,
    100
  );

  const abbreviatedLogoData = abbreviatedLogoMobile
    ? getImageDataWithHttps(abbreviatedLogoMobile)
    : undefined;

  const fullLogoData = fullLogoMobile
    ? getImageDataWithHttps(fullLogoMobile)
    : defaultFullLogoData;

  // Animation for Updated Sticky Button (scroll up animation, not fade)
  const updatedStickyButtonSpring = useSpring(
    String(bottomMobileCta?.fields?.designType) === 'Updated Sticky Button'
      ? {
          to: {
            transform:
              bottomMobileCta && ctaScrollPosition && !isMenuOpen
                ? 'translateY(0%)'
                : 'translateY(300%)',
            opacity: 1
          },
          config: { tension: 300, friction: 26, mass: 1, duration: 200 }
        }
      : {}
  );

  return (
    <>
      <MobileNavbarWrapper data-testid="mobile-nav-cta">
        <Link
          data-testid="mobile-logo-link"
          href={linkToHome ? linkToHome : '/'}
          className="flex"
        >
          {isScrolled && !showAuthenticatedUser && !!abbreviatedLogoData ? (
            <LogoWrapper>
              <Image
                src={abbreviatedLogoData.src}
                alt={abbreviatedLogoData.alt}
                height={abbreviatedLogoData.height}
                width={abbreviatedLogoData.width}
              />
            </LogoWrapper>
          ) : (
            <div style={{ width: '160px', height: '24px' }}>
              <Image
                src={fullLogoData.src}
                alt={fullLogoData.alt}
                height={24}
                width={160}
                style={{ objectFit: 'contain' }}
              />
            </div>
          )}
        </Link>

        {isScrolled &&
          !isMenuOpen &&
          !mobileCta &&
          !showAuthenticatedUser &&
          !bottomMobileCta &&
          scrolledMobileCta && (
            <CtaWidget
              {...scrolledMobileCta?.fields}
              id="mobile-nav-cta"
              sysId={scrolledMobileCta?.sys?.contentType?.sys?.id}
              module="Navigation"
              isScrolled={isScrolled}
            />
          )}

        {!isSimplified && (
          <Hamburger active={isMenuOpen} onClick={toggleModal} />
        )}

        {mobileCta && !scrolledMobileCta && !showAuthenticatedUser && (
          <CtaWidget
            {...mobileCta?.fields}
            id="mobile-nav-cta"
            sysId={mobileCta?.sys?.contentType?.sys?.id}
            module="Navigation"
            isScrolled={isScrolled}
          />
        )}

        {showAuthenticatedUser && <ProfileDropdownMenu />}
      </MobileNavbarWrapper>

      {bottomMobileCta?.fields?.designType === 'Updated Sticky Button' ? (
        <animated.div
          data-testid="bottom-cta"
          style={{
            backgroundColor:
              bottomMobileCta?.fields?.designType === 'Updated Sticky Button'
                ? '#F2F5F5'
                : 'none',
            ...updatedStickyButtonSpring
          }}
          className={cx(
            'z-3 fixed -bottom-[2px] flex w-full items-center justify-center bg-white py-[16px] desktop:hidden'
          )}
        >
          <CtaWidget
            {...bottomMobileCta?.fields}
            id="bottom-cta"
            errorPosition="top"
            module="Bottom CTA"
          />
        </animated.div>
      ) : (
        <div
          data-testid="bottom-cta"
          className={cx(
            'z-3 fixed -bottom-[2px] flex w-full items-center justify-center bg-white py-[16px] transition-all duration-300 ease-in-out desktop:hidden',
            {
              'translate-y-0 opacity-100':
                bottomMobileCta && ctaScrollPosition && !isMenuOpen,
              'translate-y-full opacity-0':
                !bottomMobileCta || !ctaScrollPosition || isMenuOpen
            }
          )}
        >
          <CtaWidget
            {...bottomMobileCta?.fields}
            id="bottom-cta"
            errorPosition="top"
            module="Bottom CTA"
          />
        </div>
      )}
    </>
  );
}
